import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { 
  Bold, Italic, List, Link2, Square, Save, X, Paperclip, FileText, 
  Send, UserPlus, Filter, CheckCircle2, Mail, Eye, Calendar
} from "lucide-react";
import "./AdminEmailComposer.css";

const STATUS_OPTIONS = [
  { value: "new",      label: "New / First Time" },
  { value: "approved", label: "Approved" },
  { value: "pending",  label: "Pending" },
  { value: "denied",   label: "Denied" },
  { value: "on_hold",  label: "On hold" },
];

const TENT_TYPE_OPTIONS = [
  { value: "20x20_single", label: "20×20 Individual" },
  { value: "20x20_shared", label: "20×20 Shared" },
  { value: "10x10_single", label: "10×10 Individual" },
  { value: "food_court",   label: "Food Court" },
  { value: "pod_shared",   label: "17×17 Pod" },
  { value: "20x20_open",   label: "20×20 Open Space" },
  { value: "sponsored",    label: "Sponsored" },
  { value: "ITC",          label: "ITC Space" },
  { value: "WFTO",         label: "WFTO Space" },
  {value: "biz_boost",  label:"Biz Boost"},
];

const ATTACHMENTS_BUCKET = "email-attachments";
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 20 * 1024 * 1024;
const ATTACHMENT_ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";

export default function ComposeEmailView() {
  // ── Recipients & Filters ──
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [tentTypes, setTentTypes] = useState([]);
  const [onlyFirstTimeVendors, setOnlyFirstTimeVendors] = useState(false);
  const [excludeFirstTimeVendors, setExcludeFirstTimeVendors] = useState(false);
  const [submittedAfter, setSubmittedAfter] = useState(""); // NEW: Date/Time state (YYYY-MM-DDTHH:mm)

  const [toQuery, setToQuery] = useState("");
  const [toResults, setToResults] = useState([]);
  const [toManual, setToManual] = useState([]);

  const [filterMatches, setFilterMatches] = useState([]);
  const [loadingFilterMatches, setLoadingFilterMatches] = useState(false);
  const [showRecipientList, setShowRecipientList] = useState(false);
  const [showCcRow, setShowCcRow] = useState(false);

  const [ccQuery, setCcQuery] = useState("");
  const [ccResults, setCcResults] = useState([]);
  const [ccSelected, setCcSelected] = useState([]);
  const [ccExtraInput, setCcExtraInput] = useState("");
  const [ccExtraEmails, setCcExtraEmails] = useState([]);

  // ── Message ──
  const [subject, setSubject] = useState("");
  const bodyRef = useRef(null);
  const [bodyEmpty, setBodyEmpty] = useState(true);

  // ── Attachments ──
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const attachmentInputRef = useRef(null);

  // ── Templates ──
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState("");

  // ── Button modal & Send state ──
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [buttonLabel, setButtonLabel] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const savedSelectionRef = useRef(null);

  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("events")
        .select("id, name, event_key")
        .order("start_date", { ascending: false });
      setEvents(data || []);
    };
    load();
  }, []);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    const { data, error } = await supabase
      .from("email_templates")
      .select("*")
      .order("created_at", { ascending: false });
    if (!error) setTemplates(data || []);
    setTemplatesLoading(false);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const refreshFilterMatches = useCallback(async () => {
    const hasAnyFilter = eventId || statuses.length || tentTypes.length || onlyFirstTimeVendors || excludeFirstTimeVendors || submittedAfter;
    if (!hasAnyFilter) { setFilterMatches([]); return; }

    setLoadingFilterMatches(true);
    let query = supabase
      .from("vendor_applications")
      .select("id, tent_type, status, created_at, vendor_id, is_first_application, vendors(id, brand_name, email)");

    if (eventId)          query = query.eq("event_id", eventId);
    if (statuses.length)  query = query.in("status", statuses.filter(s => s !== "new"));
    if (tentTypes.length) query = query.in("tent_type", tentTypes);

    // Direct DB Cohort filter
    if (onlyFirstTimeVendors) {
      query = query.eq("is_first_application", true);
    } else if (excludeFirstTimeVendors) {
      query = query.eq("is_first_application", false);
    }

    // Direct DB Date/Time Filter (created_at >= submittedAfter)
    if (submittedAfter) {
      query = query.gte("created_at", new Date(submittedAfter).toISOString());
    }

    const { data, error } = await query;
    if (error) {
      console.error("Recipient preview failed:", error);
      setFilterMatches([]);
      setLoadingFilterMatches(false);
      return;
    }

    const list = (data || [])
      .filter((r) => r.vendors?.email)
      .map((r) => ({ vendor_id: r.vendors.id, email: r.vendors.email, brand_name: r.vendors.brand_name }));

    setFilterMatches(list);
    setLoadingFilterMatches(false);
  }, [eventId, statuses, tentTypes, onlyFirstTimeVendors, excludeFirstTimeVendors, submittedAfter]);

  useEffect(() => { refreshFilterMatches(); }, [refreshFilterMatches]);

  const combinedRecipients = (() => {
    const seen = new Set();
    return [
      ...toManual.map((v) => ({ vendor_id: v.id, email: v.email, brand_name: v.brand_name })),
      ...filterMatches,
    ].filter((r) => (seen.has(r.vendor_id) ? false : (seen.add(r.vendor_id), true)));
  })();

  const toggleStatus = (val) =>
    setStatuses((prev) => (prev.includes(val) ? prev.filter((s) => s !== val) : [...prev, val]));

  const toggleTentType = (val) =>
    setTentTypes((prev) => (prev.includes(val) ? prev.filter((t) => t !== val) : [...prev, val]));

  useEffect(() => {
    if (!toQuery.trim()) { setToResults([]); return; }
    const handle = setTimeout(async () => {
      const q = toQuery.trim();
      const { data, error } = await supabase
        .from("vendors")
        .select("id, brand_name, contact_person, email")
        .or(`brand_name.ilike.%${q}%,contact_person.ilike.%${q}%,email.ilike.%${q}%`)
        .limit(8);
      if (!error) setToResults(data || []);
    }, 300);
    return () => clearTimeout(handle);
  }, [toQuery]);

  const addToVendor = (vendor) => {
    setToManual((prev) => (prev.some((v) => v.id === vendor.id) ? prev : [...prev, vendor]));
    setToQuery("");
    setToResults([]);
  };
  const removeToVendor = (id) => setToManual((prev) => prev.filter((v) => v.id !== id));

  useEffect(() => {
    if (!ccQuery.trim()) { setCcResults([]); return; }
    const handle = setTimeout(async () => {
      const q = ccQuery.trim();
      const { data, error } = await supabase
        .from("vendors")
        .select("id, brand_name, contact_person, email")
        .or(`brand_name.ilike.%${q}%,contact_person.ilike.%${q}%,email.ilike.%${q}%`)
        .limit(8);
      if (!error) setCcResults(data || []);
    }, 300);
    return () => clearTimeout(handle);
  }, [ccQuery]);

  const addCcVendor = (vendor) => {
    setCcSelected((prev) => (prev.some((v) => v.id === vendor.id) ? prev : [...prev, vendor]));
    setCcQuery("");
    setCcResults([]);
  };
  const removeCcVendor = (id) => setCcSelected((prev) => prev.filter((v) => v.id !== id));

  const addExtraEmail = () => {
    const email = ccExtraInput.trim();
    if (email && /\S+@\S+\.\S+/.test(email) && !ccExtraEmails.includes(email)) {
      setCcExtraEmails((prev) => [...prev, email]);
    }
    setCcExtraInput("");
  };
  const removeExtraEmail = (email) => setCcExtraEmails((prev) => prev.filter((e) => e !== email));

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated as admin.");
    return { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` };
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleAttachmentSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    setAttachmentError("");

    let runningTotal = attachments.reduce((sum, a) => sum + a.file.size, 0);
    const accepted = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        setAttachmentError(`"${file.name}" is over the 10MB per-file limit.`);
        continue;
      }
      if (runningTotal + file.size > MAX_TOTAL_SIZE) {
        setAttachmentError("Adding that file would put total attachments over 20MB.");
        continue;
      }
      runningTotal += file.size;
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        status: "pending",
        url: null,
        error: null,
      });
    }

    if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  const uploadAttachments = async () => {
    const alreadyDone = attachments
      .filter((a) => a.status === "done")
      .map((a) => ({ filename: a.file.name, url: a.url }));

    const pending = attachments.filter((a) => a.status !== "done");
    if (!pending.length) return alreadyDone;

    setAttachments((prev) =>
      prev.map((a) => (a.status !== "done" ? { ...a, status: "uploading", error: null } : a))
    );

    const results = await Promise.all(
      pending.map(async (a) => {
        try {
          const safeName = a.file.name.replace(/[^a-zA-Z0-9.\-_]/g, "_");
          const path = `${Date.now()}-${Math.random().toString(36).slice(2)}-${safeName}`;

          const { error: uploadError } = await supabase.storage
            .from(ATTACHMENTS_BUCKET)
            .upload(path, a.file, { cacheControl: "3600", upsert: false });
          if (uploadError) throw uploadError;

          const { data: signedData, error: signError } = await supabase.storage
            .from(ATTACHMENTS_BUCKET)
            .createSignedUrl(path, 60 * 60 * 24);
          if (signError) throw signError;

          return { id: a.id, ok: true, url: signedData.signedUrl, filename: a.file.name };
        } catch (err) {
          return { id: a.id, ok: false, error: err.message || "Upload failed" };
        }
      })
    );

    setAttachments((prev) =>
      prev.map((a) => {
        const r = results.find((res) => res.id === a.id);
        if (!r) return a;
        return r.ok
          ? { ...a, status: "done", url: r.url, error: null }
          : { ...a, status: "error", error: r.error };
      })
    );

    const failed = results.filter((r) => !r.ok);
    if (failed.length) {
      throw new Error(`${failed.length} attachment${failed.length === 1 ? "" : "s"} failed to upload.`);
    }

    const newlyDone = results.map((r) => ({ filename: r.filename, url: r.url }));
    return [...alreadyDone, ...newlyDone];
  };

  const attachmentsUploading = attachments.some((a) => a.status === "uploading");

  const exec = (command, value = null) => {
    bodyRef.current?.focus();
    document.execCommand(command, false, value);
    setBodyEmpty(!bodyRef.current?.innerText?.trim());
  };

  const handleInsertLink = () => {
    const url = window.prompt("Link URL:");
    if (!url) return;
    exec("createLink", url);
  };

  const openButtonModal = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
    setButtonLabel("");
    setButtonUrl("");
    setShowButtonModal(true);
  };

  const insertButton = () => {
    if (!buttonLabel.trim() || !buttonUrl.trim()) return;

    bodyRef.current?.focus();
    const sel = window.getSelection();
    if (savedSelectionRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }

    const url = /^https?:\/\//i.test(buttonUrl.trim()) ? buttonUrl.trim() : `https://${buttonUrl.trim()}`;

    const html = `
      <p style="margin:20px 0;">
        <a href="${url}" target="_blank" rel="noopener noreferrer"
           style="display:inline-block;padding:12px 24px;background-color:#0f172a;color:#ffffff;
                  font-weight:600;font-size:14px;text-decoration:none;border-radius:8px;">
          ${buttonLabel.trim()}
        </a>
      </p>
    `;
    document.execCommand("insertHTML", false, html);
    setBodyEmpty(!bodyRef.current?.innerText?.trim());
    setShowButtonModal(false);
  };

  const handleBodyInput = () => {
    setBodyEmpty(!bodyRef.current?.innerText?.trim());
  };

  const getBodyHtml = () => bodyRef.current?.innerHTML || "";
  const setBodyHtml = (html) => {
    if (bodyRef.current) {
      bodyRef.current.innerHTML = html || "";
      setBodyEmpty(!bodyRef.current.innerText?.trim());
    }
  };

  const applyTemplate = (id) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    setSubject(tpl.subject || "");
    setBodyHtml(tpl.body_html || "");
  };

  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim()) return;
    setSavingTemplate(true);
    setTemplateError("");
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from("email_templates").insert([{
        name: newTemplateName.trim(),
        subject: subject.trim(),
        body_html: getBodyHtml(),
        created_by: user?.email || null,
      }]);
      if (error) throw error;
      setShowSaveTemplate(false);
      setNewTemplateName("");
      loadTemplates();
    } catch (err) {
      setTemplateError(err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSendTest = async () => {
    setTestSending(true);
    setSendError("");
    setSendSuccess("");
    try {
      const attachmentPayload = await uploadAttachments();
      const headers = await getAuthHeaders();
      const { data: { session } } = await supabase.auth.getSession();
      const testEmail = session?.user?.email;
      if (!testEmail) throw new Error("Couldn't determine your own email for the test send.");

      const res = await fetch("/api/admin-send-bulk-email", {
        method: "POST",
        headers,
        body: JSON.stringify({
          testEmail,
          subject,
          bodyHtml: getBodyHtml(),
          attachments: attachmentPayload,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Test send failed");
      setSendSuccess(`Test sent to ${testEmail}.`);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setTestSending(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setSendError("");
    setSendSuccess("");
    try {
      const attachmentPayload = await uploadAttachments();
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin-send-bulk-email", {
        method: "POST",
        headers,
        body: JSON.stringify({
          filters: (eventId || statuses.length || tentTypes.length || onlyFirstTimeVendors || excludeFirstTimeVendors || submittedAfter)
            ? { 
                eventId: eventId || null, 
                statuses, 
                tentTypes, 
                onlyFirstTimeVendors, 
                excludeFirstTimeVendors,
                submittedAfter: submittedAfter ? new Date(submittedAfter).toISOString() : null // Passed to backend
              }
            : null,
          toVendorIds: toManual.map((v) => v.id),
          ccVendorIds: ccSelected.map((v) => v.id),
          ccEmails: ccExtraEmails,
          subject,
          bodyHtml: getBodyHtml(),
          attachments: attachmentPayload,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Send failed");

      setSendSuccess(
        `Sent to ${data.sent} vendor${data.sent === 1 ? "" : "s"}${data.ccCount ? `, cc'd ${data.ccCount}` : ""}.`
      );
      setConfirmOpen(false);
      setSubject("");
      setBodyHtml("");
      setSelectedTemplateId("");
      setToManual([]);
      setCcSelected([]);
      setCcExtraEmails([]);
      setAttachments([]);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  const canReview = subject.trim() && !bodyEmpty && combinedRecipients.length > 0 && !attachmentsUploading;

  return (
    <div className="aec-email-client-shell">
      {/* App Header Bar */}
      <header className="aec-app-header">
        <div className="aec-header-brand">
          <Mail size={20} className="aec-brand-icon" />
          <div>
            <h2>Admin Broadcast Mailer</h2>
            <span>Compose & send official communications to vendors</span>
          </div>
        </div>

        <div className="aec-header-actions">
          <select
            className="aec-template-select-top"
            value={selectedTemplateId}
            onChange={(e) => applyTemplate(e.target.value)}
            disabled={templatesLoading}
          >
            <option value="">
              {templatesLoading ? "Loading templates…" : "Select a Template…"}
            </option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>

          <button
            type="button"
            className="aec-btn-secondary"
            onClick={() => setShowSaveTemplate(true)}
            disabled={!subject.trim() && bodyEmpty}
          >
            <Save size={14} /> Save Template
          </button>
        </div>
      </header>

      {/* Main Workspace Grid */}
      <div className="aec-workspace-grid">
        {/* LEFT SIDEBAR: RECIPIENT AUDIENCE FILTERS */}
        <aside className="aec-audience-sidebar">
          <div className="aec-sidebar-header">
            <Filter size={16} />
            <h3>Audience & Targeting</h3>
          </div>

          {/* Combined Live Counter */}
          <div className="aec-recipient-stat-card">
            <div className="aec-stat-number">{combinedRecipients.length}</div>
            <div className="aec-stat-label">Total Selected Recipients</div>
            {combinedRecipients.length > 0 && (
              <button 
                type="button" 
                className="aec-preview-trigger"
                onClick={() => setShowRecipientList((v) => !v)}
              >
                <Eye size={13} /> {showRecipientList ? "Hide List" : "Preview Recipient List"}
              </button>
            )}
          </div>

          {/* List Preview Overlay Drawer */}
          {showRecipientList && (
            <div className="aec-recipient-drawer">
              <div className="aec-drawer-head">
                <span>Matched Recipients</span>
                <button type="button" onClick={() => setShowRecipientList(false)}><X size={14}/></button>
              </div>
              <ul className="aec-recipient-scroll-list">
                {combinedRecipients.map((r) => (
                  <li key={r.vendor_id}>
                    <strong>{r.brand_name || "Unnamed Vendor"}</strong>
                    <span>{r.email}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Event Filter Section */}
          <div className="aec-filter-group">
            <label className="aec-sidebar-label">Event Edition</label>
            <div className="aec-select-wrapper">
              <select className="aec-sidebar-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
                <option value="">All Event Editions</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* NEW: Application Date/Time Filter */}
          <div className="aec-filter-group">
            <label className="aec-sidebar-label">Submitted After (Date & Time)</label>
            <div className="aec-date-wrapper" style={{ position: "relative" }}>
              <input
                type="datetime-local"
                className="aec-sidebar-select"
                value={submittedAfter}
                onChange={(e) => setSubmittedAfter(e.target.value)}
              />
              {submittedAfter && (
                <button 
                  type="button" 
                  onClick={() => setSubmittedAfter("")}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#94a3b8"
                  }}
                  title="Clear Date Filter"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Vendor Cohort Targeting */}
          <div className="aec-filter-group">
            <label className="aec-sidebar-label">Vendor Cohort Isolation</label>
            
            <div className="aec-checkbox-card" style={{ marginBottom: 6 }}>
              <label className="aec-checkbox-label">
                <input
                  type="checkbox"
                  checked={onlyFirstTimeVendors}
                  onChange={(e) => {
                    setOnlyFirstTimeVendors(e.target.checked);
                    if (e.target.checked) setExcludeFirstTimeVendors(false);
                  }}
                />
                <span className="aec-checkbox-custom"></span>
                <div className="aec-checkbox-text">
                  <strong>First-time vendors only</strong>
                  <span>Only vendors debuting in this edition</span>
                </div>
              </label>
            </div>

            <div className="aec-checkbox-card">
              <label className="aec-checkbox-label">
                <input
                  type="checkbox"
                  checked={excludeFirstTimeVendors}
                  onChange={(e) => {
                    setExcludeFirstTimeVendors(e.target.checked);
                    if (e.target.checked) setOnlyFirstTimeVendors(false);
                  }}
                />
                <span className="aec-checkbox-custom"></span>
                <div className="aec-checkbox-text">
                  <strong>Returning vendors only (Exclude New)</strong>
                  <span>Only vendors who have participated in 2+ editions</span>
                </div>
              </label>
            </div>
          </div>

          {/* Application Status Pill Filters */}
          <div className="aec-filter-group">
            <label className="aec-sidebar-label">Application Status</label>
            <div className="aec-pill-selector">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`aec-filter-pill ${statuses.includes(opt.value) ? "active" : ""}`}
                  onClick={() => toggleStatus(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Space / Tent Type Pill Filters */}
          <div className="aec-filter-group">
            <label className="aec-sidebar-label">Space / Tent Type</label>
            <div className="aec-pill-selector">
              {TENT_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`aec-filter-pill ${tentTypes.includes(opt.value) ? "active" : ""}`}
                  onClick={() => toggleTentType(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {loadingFilterMatches && <div className="aec-status-loader">Calculating audience match…</div>}
        </aside>

        {/* RIGHT MAIN PANEL: EMAIL COMPOSER CANVAS */}
        <main className="aec-composer-canvas">
          {/* Header Row Inputs (To & Cc) */}
          <div className="aec-header-inputs">
            {/* TO Row */}
            <div className="aec-input-row">
              <span className="aec-row-label">To:</span>
              <div className="aec-pills-input-box">
                {toManual.map((v) => (
                  <span key={v.id} className="aec-recipient-badge">
                    {v.brand_name || v.email}
                    <button type="button" onClick={() => removeToVendor(v.id)}>×</button>
                  </span>
                ))}
                <input
                  type="text"
                  className="aec-inline-field"
                  placeholder={toManual.length === 0 ? "Search vendor names or emails to add directly..." : "Add another..."}
                  value={toQuery}
                  onChange={(e) => setToQuery(e.target.value)}
                />
              </div>

              {!showCcRow && (
                <button type="button" className="aec-cc-toggle-btn" onClick={() => setShowCcRow(true)}>
                  Cc
                </button>
              )}

              {toResults.length > 0 && (
                <ul className="aec-autocomplete-dropdown">
                  {toResults.map((v) => (
                    <li key={v.id} onClick={() => addToVendor(v)}>
                      <strong>{v.brand_name || v.contact_person || "Unnamed"}</strong>
                      <span>{v.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* CC Row */}
            {showCcRow && (
              <div className="aec-input-row">
                <span className="aec-row-label">Cc:</span>
                <div className="aec-pills-input-box">
                  {ccSelected.map((v) => (
                    <span key={v.id} className="aec-recipient-badge aec-recipient-badge--cc">
                      {v.brand_name || v.email}
                      <button type="button" onClick={() => removeCcVendor(v.id)}>×</button>
                    </span>
                  ))}
                  {ccExtraEmails.map((email) => (
                    <span key={email} className="aec-recipient-badge aec-recipient-badge--cc">
                      {email}
                      <button type="button" onClick={() => removeExtraEmail(email)}>×</button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="aec-inline-field"
                    placeholder="Search vendor or enter email address..."
                    value={ccQuery}
                    onChange={(e) => setCcQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && ccQuery.includes("@")) {
                        e.preventDefault();
                        setCcExtraInput(ccQuery);
                        addExtraEmail();
                        setCcQuery("");
                      }
                    }}
                  />
                </div>

                {ccResults.length > 0 && (
                  <ul className="aec-autocomplete-dropdown">
                    {ccResults.map((v) => (
                      <li key={v.id} onClick={() => addCcVendor(v)}>
                        <strong>{v.brand_name || v.contact_person || "Unnamed"}</strong>
                        <span>{v.email}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Subject Input */}
            <div className="aec-input-row aec-subject-row">
              <span className="aec-row-label">Subject:</span>
              <input
                type="text"
                className="aec-subject-field"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Write a clear, concise subject line..."
              />
            </div>
          </div>

          {/* Rich Text Format Toolbar */}
          <div className="aec-editor-toolbar">
            <div className="aec-toolbar-group">
              <button type="button" className="aec-tool-icon" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} title="Bold">
                <Bold size={15} />
              </button>
              <button type="button" className="aec-tool-icon" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} title="Italic">
                <Italic size={15} />
              </button>
              <button type="button" className="aec-tool-icon" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} title="Bullet list">
                <List size={15} />
              </button>
              <button type="button" className="aec-tool-icon" onMouseDown={(e) => e.preventDefault()} onClick={handleInsertLink} title="Insert link">
                <Link2 size={15} />
              </button>
            </div>

            <div className="aec-toolbar-divider" />

            <button type="button" className="aec-tool-btn-text" onMouseDown={(e) => e.preventDefault()} onClick={openButtonModal}>
              <Square size={13} /> Add Call-to-Action Button
            </button>

            <div className="aec-toolbar-right">
              <button type="button" className="aec-attach-btn" onClick={() => attachmentInputRef.current?.click()}>
                <Paperclip size={14} /> Attach Files
              </button>
              <input
                ref={attachmentInputRef}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={handleAttachmentSelect}
                style={{ display: "none" }}
              />
            </div>
          </div>

          {/* Editable Body */}
          <div className="aec-editor-workspace">
            <div
              ref={bodyRef}
              className="aec-body-editable-area"
              contentEditable
              onInput={handleBodyInput}
              data-placeholder="Write your email body here. Content will automatically be styled with the official BizBaz header & footer template..."
              suppressContentEditableWarning
            />

            {/* Attached Files Strip */}
            {attachments.length > 0 && (
              <div className="aec-attachments-tray">
                <div className="aec-tray-title">Attachments ({attachments.length})</div>
                <div className="aec-tray-grid">
                  {attachments.map((a) => (
                    <div key={a.id} className="aec-file-chip">
                      <FileText size={14} />
                      <div className="aec-file-info">
                        <span className="aec-file-name">{a.file.name}</span>
                        <span className="aec-file-meta">{formatFileSize(a.file.size)} • {a.status}</span>
                      </div>
                      <button type="button" onClick={() => removeAttachment(a.id)}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer Action Strip */}
          <div className="aec-action-footer">
            <div className="aec-status-messages">
              {attachmentError && <span className="aec-error-text">{attachmentError}</span>}
              {sendError && <span className="aec-error-text">{sendError}</span>}
              {sendSuccess && <span className="aec-success-text"><CheckCircle2 size={14} /> {sendSuccess}</span>}
            </div>

            <div className="aec-footer-btns">
              <button
                type="button"
                className="aec-btn-secondary"
                onClick={handleSendTest}
                disabled={testSending || !subject.trim() || bodyEmpty || attachmentsUploading}
              >
                {testSending ? "Sending Test…" : "Send Test Email"}
              </button>

              <button
                type="button"
                className="aec-btn-primary"
                onClick={() => setConfirmOpen(true)}
                disabled={!canReview}
              >
                <Send size={15} /> Review & Send Broadcast
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* MODALS */}
      {showSaveTemplate && (
        <div className="aec-modal-overlay" onClick={() => setShowSaveTemplate(false)}>
          <div className="aec-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="aec-modal-head">
              <h3>Save as Reusable Template</h3>
              <button type="button" onClick={() => setShowSaveTemplate(false)}><X size={16} /></button>
            </div>
            <div className="aec-modal-body">
              <label>Template Name</label>
              <input
                type="text"
                className="aec-modal-input"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g. Apology to Returning Vendors"
                autoFocus
              />
              {templateError && <p className="aec-error-text">{templateError}</p>}
            </div>
            <div className="aec-modal-foot">
              <button type="button" className="aec-btn-secondary" onClick={() => setShowSaveTemplate(false)}>Cancel</button>
              <button type="button" className="aec-btn-primary" onClick={handleSaveTemplate} disabled={savingTemplate || !newTemplateName.trim()}>
                {savingTemplate ? "Saving…" : "Save Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showButtonModal && (
        <div className="aec-modal-overlay" onClick={() => setShowButtonModal(false)}>
          <div className="aec-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="aec-modal-head">
              <h3>Insert Action Button</h3>
              <button type="button" onClick={() => setShowButtonModal(false)}><X size={16} /></button>
            </div>
            <div className="aec-modal-body">
              <label>Button Label Text</label>
              <input
                type="text"
                className="aec-modal-input"
                value={buttonLabel}
                onChange={(e) => setButtonLabel(e.target.value)}
                placeholder="e.g. Access Vendor Portal"
                autoFocus
              />
              <label style={{ marginTop: 12 }}>Target Web Link (URL)</label>
              <input
                type="text"
                className="aec-modal-input"
                value={buttonUrl}
                onChange={(e) => setButtonUrl(e.target.value)}
                placeholder="https://bizbazevents.com/vendor/portal"
              />
            </div>
            <div className="aec-modal-foot">
              <button type="button" className="aec-btn-secondary" onClick={() => setShowButtonModal(false)}>Cancel</button>
              <button type="button" className="aec-btn-primary" onClick={insertButton} disabled={!buttonLabel.trim() || !buttonUrl.trim()}>
                Insert Button
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="aec-modal-overlay" onClick={() => setConfirmOpen(false)}>
          <div className="aec-modal-card aec-modal-card--wide" onClick={(e) => e.stopPropagation()}>
            <div className="aec-modal-head">
              <h3>Confirm Broadcast Email</h3>
              <button type="button" onClick={() => setConfirmOpen(false)}><X size={16} /></button>
            </div>
            <div className="aec-modal-body">
              <div className="aec-summary-box">
                <div><strong>Recipients:</strong> {combinedRecipients.length} matched vendor(s)</div>
                {ccSelected.length + ccExtraEmails.length > 0 && (
                  <div><strong>Cc:</strong> {[...ccSelected.map((v) => v.email), ...ccExtraEmails].join(", ")}</div>
                )}
                <div><strong>Subject:</strong> {subject}</div>
                {attachments.length > 0 && (
                  <div><strong>Attachments:</strong> {attachments.length} file(s) attached</div>
                )}
              </div>
              <p className="aec-warning-text">This action will immediately dispatch emails to all matched vendors.</p>
            </div>
            <div className="aec-modal-foot">
              <button type="button" className="aec-btn-secondary" onClick={() => setConfirmOpen(false)} disabled={sending}>
                Cancel
              </button>
              <button type="button" className="aec-btn-primary" onClick={handleSend} disabled={sending}>
                {sending ? "Dispatching Emails..." : `Send to ${combinedRecipients.length} Vendors`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}